import {
  Alert,
  Button,
  Card,
  Col,
  Form,
  InputNumber,
  Row,
  Segmented,
  Select,
  Space,
  Switch,
  Tag,
  Typography,
  notification,
} from "antd";
import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import useApi from "../../utils/useApi";
import { describeError, formatDateTime } from "../ai-reports/status";
import { formatUsd } from "./labels";

const { Text } = Typography;
const FILA_PARADA_SEGUNDOS = 120;

const GRUPOS = [
  {
    titulo: "Respostas",
    campos: [
      {
        name: "max_tool_calls",
        label: "Limite de consultas",
        unidade: "máx.",
        min: 1,
        max: 20,
        help: "Quantas consultas o agente pode fazer para responder uma mensagem.",
      },
      {
        name: "max_output_tokens",
        label: "Tamanho da resposta",
        unidade: "tokens",
        min: 256,
        max: 16000,
        step: 256,
        help: "Limite de cada resposta do modelo.",
      },
      {
        name: "history_messages",
        label: "Memória",
        unidade: "msgs",
        min: 0,
        max: 100,
        help: "Mensagens anteriores que o agente relê em cada resposta.",
      },
    ],
  },
  {
    titulo: "Prazos para o usuário",
    campos: [
      {
        name: "conversation_ttl_hours",
        label: "Nova conversa após",
        unidade: "h",
        min: 1,
        max: 168,
        help: "Sem mensagens nesse tempo, a próxima abre uma conversa nova.",
      },
      {
        name: "action_ttl_minutes",
        label: "Prazo para confirmar",
        unidade: "min",
        min: 1,
        max: 120,
        help: "Depois disso, a alteração solicitada expira sem ser feita.",
      },
      {
        name: "undo_window_minutes",
        label: "Prazo para desfazer",
        unidade: "min",
        min: 1,
        max: 1440,
        help: "Janela em que o usuário pode pedir ao agente para desfazer o que gravou.",
      },
    ],
  },
  {
    titulo: "Limites e operação",
    campos: [
      {
        name: "rate_per_minute",
        label: "Limite por usuário",
        unidade: "/min",
        min: 1,
        max: 300,
        help: "Mensagens por minuto de cada usuário. Acima disso o chat pede para aguardar.",
      },
      {
        name: "provider_timeout",
        label: "Espera pelo modelo",
        unidade: "s",
        min: 10,
        max: 300,
        help: "Tempo máximo de uma chamada ao modelo.",
      },
      {
        name: "max_tentativas",
        label: "Tentativas",
        unidade: "vezes",
        min: 1,
        max: 10,
        help: "Vezes que a mensagem volta para a fila quando o modelo falha.",
      },
      {
        name: "worker_poll_seconds",
        label: "Intervalo do worker",
        unidade: "s",
        min: 0.2,
        max: 30,
        step: 0.1,
        decimal: true,
        help: "Pausa do worker entre consultas à fila vazia. Vale em até 1 min.",
      },
    ],
  },
];

const CAMPOS = ["enabled", "model", "effort"].concat(
  GRUPOS.flatMap((grupo) => grupo.campos.map((campo) => campo.name))
);

const estiloQuadro = {
  border: "1px solid var(--ouran-card-header-bg)",
  borderRadius: 8,
  padding: "8px 16px 0",
  margin: 0,
  height: "100%",
  minWidth: 0,
};

const estiloTituloQuadro = {
  padding: "0 6px",
  width: "auto",
  margin: 0,
  border: 0,
  fontSize: 14,
  fontWeight: 600,
};

function mudou(valor, original) {
  return (valor ?? null) !== (original ?? null);
}

function SeloDaFila({ ligado, fila }) {
  if (!ligado) return <Tag>Chat indisponível</Tag>;
  if (!fila) return null;
  const espera = fila.mais_antiga_segundos;
  if (
    espera !== null &&
    espera !== undefined &&
    espera > FILA_PARADA_SEGUNDOS
  ) {
    return <Tag color="warning">Fila parada há {espera} s</Tag>;
  }
  if (fila.em_aberto > 0) {
    return <Tag color="processing">{fila.em_aberto} na fila</Tag>;
  }
  return <Tag color="success">Fila em dia</Tag>;
}

function RotuloDoModelo({ modelo }) {
  return (
    <span
      style={{
        display: "flex",
        justifyContent: "space-between",
        gap: 12,
        minWidth: 0,
      }}>
      <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
        {modelo.label}
      </span>
      <Text type="secondary" style={{ fontSize: 13, whiteSpace: "nowrap" }}>
        {formatUsd(modelo.input_usd_per_mtok, 2)} entrada /{" "}
        {formatUsd(modelo.output_usd_per_mtok, 2)} saída por 1M tokens
      </Text>
    </span>
  );
}

export default function ConfigCard() {
  const { getAgentConfig, updateAgentConfig } = useApi();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const valores = Form.useWatch([], form) || {};

  const { data, isFetching } = useQuery({
    queryKey: ["agent-config"],
    queryFn: getAgentConfig,
    staleTime: 0,
  });

  const original = data?.config;

  useEffect(() => {
    if (original) form.setFieldsValue(original);
  }, [original, form]);

  const alterados = original
    ? CAMPOS.filter((nome) => mudou(valores[nome], original[nome]))
    : [];

  const salvar = async (campos) => {
    try {
      const resposta = await updateAgentConfig(campos);
      queryClient.setQueryData(["agent-config"], (antigo) => ({
        ...antigo,
        ...resposta,
      }));
      queryClient.invalidateQueries({ queryKey: ["agent-status"] });
      notification.success({ message: "Configuração salva." });
    } catch (err) {
      notification.error({
        message: "Erro ao salvar a configuração.",
        description: describeError(err, ""),
      });
    }
  };

  const descartar = () => form.setFieldsValue(original);

  const modelos = data?.modelos || [];
  const fila = data?.fila;
  const ligado = valores.enabled ?? original?.enabled;
  const filaParada =
    fila?.mais_antiga_segundos !== null &&
    fila?.mais_antiga_segundos !== undefined &&
    fila.mais_antiga_segundos > FILA_PARADA_SEGUNDOS;

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={salvar}
      requiredMark={false}
      disabled={isFetching && !data}>
      <Card
        title="Configuração do agente"
        extra={
          <Space size={12}>
            <Space size={8}>
              <Form.Item name="enabled" valuePropName="checked" noStyle>
                <Switch aria-label="Agente ligado" />
              </Form.Item>
              <Text>{ligado ? "Agente ligado" : "Agente desligado"}</Text>
            </Space>
            <SeloDaFila ligado={ligado} fila={fila} />
          </Space>
        }>
        {filaParada && (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
            message={`Há ${fila.em_aberto} mensagem(ns) esperando há ${fila.mais_antiga_segundos} s.`}
            description="O worker ouran-agent-worker pode estar parado."
          />
        )}
        <Row
          gutter={24}
          align="bottom"
          justify="space-between"
          style={{
            borderBottom: "1px solid var(--ouran-card-header-bg)",
            marginBottom: 20,
          }}>
          <Col flex="1 1 320px" style={{ maxWidth: 760, minWidth: 0 }}>
            <Form.Item
              name="model"
              label="Modelo"
              extra="Do mais barato ao mais caro. Vale a partir da próxima resposta, inclusive em conversas abertas. Preços e modelos são cadastrados em Relatórios por IA.">
              <Select
                allowClear
                placeholder="Escolha o modelo"
                status={mudou(valores.model, original?.model) ? "warning" : ""}
                options={modelos.map((m) => ({
                  value: m.id,
                  label: <RotuloDoModelo modelo={m} />,
                }))}
              />
            </Form.Item>
          </Col>
          <Col flex="none">
            <Form.Item
              name="effort"
              label="Esforço de raciocínio"
              extra="Mais esforço deixa a resposta mais lenta e cara.">
              <Segmented
                options={(data?.esforcos || []).map((e) => ({
                  value: e.value,
                  label: e.value ? e.label : "Do modelo",
                }))}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={[16, 16]}>
          {GRUPOS.map((grupo) => (
            <Col xs={24} md={12} xl={8} key={grupo.titulo}>
              <fieldset style={estiloQuadro}>
                <legend style={estiloTituloQuadro}>{grupo.titulo}</legend>
                <Row gutter={16}>
                  {grupo.campos.map((campo) => (
                    <Col xs={24} sm={12} key={campo.name}>
                      <Form.Item
                        name={campo.name}
                        label={campo.label}
                        tooltip={campo.help}
                        rules={[{ required: true, message: "Obrigatório." }]}>
                        <InputNumber
                          min={campo.min}
                          max={campo.max}
                          step={campo.step || 1}
                          decimalSeparator={campo.decimal ? "," : undefined}
                          status={
                            mudou(valores[campo.name], original?.[campo.name])
                              ? "warning"
                              : ""
                          }
                          addonAfter={
                            <span
                              style={{
                                display: "inline-block",
                                width: 40,
                                textAlign: "center",
                              }}>
                              {campo.unidade}
                            </span>
                          }
                          style={{ width: 150 }}
                        />
                      </Form.Item>
                    </Col>
                  ))}
                </Row>
              </fieldset>
            </Col>
          ))}
        </Row>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap",
            marginTop: 24,
            paddingTop: 16,
            borderTop: "1px solid var(--ouran-card-header-bg)",
          }}>
          <Button
            type="primary"
            htmlType="submit"
            disabled={alterados.length === 0}>
            Salvar alterações
          </Button>
          <Button onClick={descartar} disabled={alterados.length === 0}>
            Descartar
          </Button>
          {alterados.length > 0 && (
            <Text type="warning">
              {alterados.length === 1
                ? "1 campo alterado"
                : `${alterados.length} campos alterados`}
            </Text>
          )}
          <span style={{ flex: 1 }} />
          {original?.updated_at && (
            <Text type="secondary">
              Alterado em {formatDateTime(original.updated_at)}
              {original.updated_by ? ` por ${original.updated_by}` : ""}
            </Text>
          )}
        </div>
      </Card>
    </Form>
  );
}
